import type { EventLiveness, HourPoint, LivenessReport, LivenessStatus } from '../api/types'

/**
 * Is tracking still firing?
 *
 * The one place in this app that shows numbers GA4 has not finished counting, and it is
 * deliberately shaped so that it cannot be mistaken for a settled day: no verdict, no
 * percentage change, no comparison to a settled figure. It answers "is this still
 * happening", not "how much of it happened". It has its own tab for the same reason —
 * the daybook keeps a one-line recap and none of the figures.
 *
 * The asymmetry matters and is visible in the wording. A count only ever grows as a day
 * settles, so "fired 1,240 times" is a fact; "fired zero times" is a suspicion, and it
 * is only ever raised when the property itself had traffic in those same hours. When
 * GA4 is behind, the strip says so instead of guessing.
 */
export function PulseStrip({ report, onRefresh, refreshing }: {
  report: LivenessReport
  onRefresh: () => void
  refreshing: boolean
}) {
  if (report.events.length === 0) return null

  const alarming = report.events.some((item) => item.status === 'silent')

  return (
    <section className={`pulse${alarming ? ' pulse--alarming' : ''}`} aria-label="Tracking pulse">
      <div className="pulse__head">
        <div>
          <h2 className="pulse__title">
            Tracking pulse
            <span className="pulse__provisional">provisional</span>
          </h2>
          <p className="pulse__note">{summarise(report)}</p>
        </div>
        <button
          type="button"
          className="link-button link-button--quiet"
          onClick={onRefresh}
          disabled={refreshing}
        >
          {refreshing ? 'Checking…' : 'Check now'}
        </button>
      </div>

      <div className="pulse__events">
        {report.events.map((event) => (
          <PulseCard key={event.event_name} event={event} stale={report.data_is_stale} />
        ))}
      </div>
    </section>
  )
}

/**
 * What the strip as a whole is entitled to say.
 *
 * `data_is_stale` is the case worth being careful about: GA4 running late looks exactly
 * like tracking having stopped, and the honest answer is to report the lag rather than
 * pick one of the two.
 */
function summarise(report: LivenessReport): string {
  if (report.data_is_stale || report.frontier === null) {
    return 'GA4 has not processed recent hours for this property yet, so nothing can be concluded from silence. The daybook is unaffected — those days are settled.'
  }

  const silent = report.events.filter((item) => item.status === 'silent')
  const frontier = formatHour(report.frontier)

  if (silent.length > 0) {
    // Phrased per event, because the window is configurable per event — quoting a
    // shared constant here would name a threshold that was never applied.
    const names = silent
      .map((item) => `${item.label} (${item.watch_hours}h)`)
      .join(', ')
    return `Nothing recorded for ${names}, while the site had traffic through ${frontier}. Worth checking the tag.`
  }

  return `Everything tracked has fired recently. GA4 has data through ${frontier}; these hours are still being counted, so the figures can only rise.`
}

const STATUS_LABEL: Record<LivenessStatus, string> = {
  firing: 'Firing',
  silent: 'Not firing',
  quiet: 'Quiet',
  never_seen: 'Never seen',
}

function PulseCard({ event, stale }: { event: EventLiveness; stale: boolean }) {
  return (
    <article className={`pulse__card pulse__card--${event.status}`}>
      <div className="pulse__card-head">
        <span className="pulse__label">{event.label}</span>
        <span className={`pulse__status pulse__status--${event.status}`}>
          <span className="pulse__dot" aria-hidden="true" />
          {STATUS_LABEL[event.status]}
        </span>
      </div>

      <Sparkbars points={event.hourly} status={event.status} />

      <p className="pulse__figure">
        {event.events_last_24h.toLocaleString()}{' '}
        <span className="pulse__figure-unit">events in the last 24h</span>
      </p>
      <p className="pulse__detail">{detail(event, stale)}</p>
      {/* The measured basis for the threshold, so it can be set from this property's own
          rhythm rather than guessed. Costs nothing — these hours are already fetched. */}
      {event.longest_normal_gap !== null && (
        <p className="pulse__detail pulse__detail--quiet">
          Longest normal gap: {event.longest_normal_gap}h · alerts after {event.watch_hours}h
        </p>
      )}
    </article>
  )
}

/**
 * The one line under each event.
 *
 * "Last seen" is expressed in hours behind GA4's own freshest data rather than behind
 * the reader's clock — the property reports in its own timezone and GA4 lags by an
 * unpredictable amount, so a wall-clock figure would be wrong twice over.
 */
function detail(event: EventLiveness, stale: boolean): string {
  if (event.status === 'never_seen') {
    return 'No occurrences in the last 10 days. If this event is new, it may not be deployed yet.'
  }
  if (event.hours_since_last_seen === null) return 'No recent occurrences.'
  if (event.hours_since_last_seen === 0) return 'Firing in the most recent hour GA4 has data for.'

  const ago = `Last seen ${event.hours_since_last_seen}h behind GA4’s latest data`
  if (event.status === 'silent') {
    return `${ago}, past its ${event.watch_hours}h limit. The same hours last week carried ${event.expected.toLocaleString()}.`
  }
  if (stale) return `${ago}, but GA4 is behind — this may not mean anything.`
  if (event.expected < event.minimum_expected) {
    return `${ago}. Too few at this time of day last week to read anything into it.`
  }
  return `${ago}, within its ${event.watch_hours}h limit.`
}

/**
 * Hourly bars, not a line.
 *
 * These are discrete hours and the thing being read is presence or absence, which a
 * gap in a bar chart shows and a line would smooth straight over.
 */
function Sparkbars({ points, status }: { points: HourPoint[]; status: LivenessStatus }) {
  if (points.length === 0) return <div className="pulse__bars" />

  const highest = Math.max(...points.map((point) => point.value), 1)

  return (
    <div className="pulse__bars" role="img" aria-label={`Hourly counts, ${points.length} hours`}>
      {points.map((point) => (
        <span
          key={point.hour}
          className={`pulse__bar${point.value === 0 ? ' pulse__bar--empty' : ''} pulse__bar--${status}`}
          style={{ height: point.value === 0 ? '2px' : `${Math.max((point.value / highest) * 100, 6)}%` }}
          title={`${formatHour(point.hour)} · ${point.value.toLocaleString()}`}
        />
      ))}
    </div>
  )
}

/**
 * Hours arrive as naive ISO strings in the property's reporting timezone.
 *
 * Parsed by hand rather than through `new Date(...)`, which would treat them as local
 * time and shift every label for anyone not sitting in the property's timezone.
 */
function formatHour(iso: string): string {
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2})/)
  if (!match) return iso
  const [, , month, day, hour] = match
  return `${Number(day)}/${Number(month)}, ${hour}:00`
}

/**
 * The daybook's one-line stand-in for the strip.
 *
 * It carries the verdict and nothing else — no counts, no bars, no per-event detail —
 * because everything provisional it could show would sit directly above settled days
 * and invite exactly the comparison the strip is shaped to prevent. A reader who wants
 * the numbers follows the link; a reader who wants to know collection is alive gets
 * that in a line.
 */
export function PulseRecap({ report, onOpen }: { report: LivenessReport; onOpen: () => void }) {
  if (report.events.length === 0) return null

  const silent = report.events.filter((item) => item.status === 'silent')
  const stale = report.data_is_stale || report.frontier === null
  // Three tones, in the order of what they cost to ignore. Staleness is not a fault of
  // the property's, so it reads as quiet rather than as an alarm.
  const tone = stale ? 'stale' : silent.length > 0 ? 'silent' : 'firing'

  return (
    <div className={`pulse-recap pulse-recap--${tone}`}>
      <span className="pulse__dot" aria-hidden="true" />
      <span className="pulse-recap__text">
        <strong>Tracking pulse:</strong> {recap(report, silent.length, stale)}
      </span>
      <button type="button" className="link-button link-button--quiet" onClick={onOpen}>
        Open the pulse →
      </button>
    </div>
  )
}

function recap(report: LivenessReport, silent: number, stale: boolean): string {
  const watched = report.events.length
  const events = `${watched} tracked ${watched === 1 ? 'event' : 'events'}`

  if (stale) {
    return `GA4 has not processed recent hours for this property, so nothing can be concluded about the ${events}. The days below are unaffected — they are settled.`
  }
  if (silent > 0) {
    return `${silent} of ${events} ${silent === 1 ? 'has' : 'have'} stopped firing while the site still has traffic.`
  }
  return `all ${events} have fired recently.`
}
